import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Collapse,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
  Drawer,
  List,
  ListItemButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { RootState } from '../../app/store';
import { ConsumedMeal } from '../../models/ConsumedMeal';
import { Foodstuff } from '../../models/Foodstuff';
import { Meal } from '../../models/Meal';
import { saveConsumedMeal, setConsumedMeal } from '../../state/meals/mealSlice';
import { FoodstuffAutocomplete } from './FoodstuffAutocomplete';
import { useTranslation } from 'react-i18next';
import { AddFoodstuff } from './AddFoodstuff';
import { MealControllerService } from '../../services/MealControllerService';
import { buildConsumedMealFoodstuffs } from '../../common/consumedMealPayload';


const mealtimeOptions = ['BREAKFAST', 'SNACK_AM', 'LUNCH', 'SNACK_PM', 'DINNER'];

export function RecordMeal() {
  const dispatch = useDispatch<any>();

  const consumedMeal = useSelector((state: RootState) => state.meal.consumedMeal);

  const [mode, setMode] = useState<'known' | 'adhoc'>('known')
  const [storedMealsOpen, setStoredMealsOpen] = useState<boolean>(false)
  const [storedMealsQuery, setStoredMealsQuery] = useState<string>('')
  const [manualFoodstuffOpen, setManualFoodstuffOpen] = useState<boolean>(false)
  const [savedMeals, setSavedMeals] = useState<Meal[]>([])
  const [savedMealsLoading, setSavedMealsLoading] = useState<boolean>(false)
  const [mealID, setMealID] = useState<string>(consumedMeal.meal?.id || '')
  const [mealName, setMealName] = useState<string>(consumedMeal.name || '')
  const [foodstuffs, setFoodstuffs] = useState<Foodstuff[]>(consumedMeal.foodstuffs || [])
  const [mealtime, setMealtime] = useState<string>(consumedMeal.mealtime || guessMealtime())
  const [consumptionTime, setConsumptionTime] = useState<string>(consumedMeal.consumptionTime || new Date().toISOString().slice(0, 16))


  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    setMealID(consumedMeal.meal?.id || '');
    setMealName(consumedMeal.name || '');
    setFoodstuffs(consumedMeal.foodstuffs || []);
    setMealtime(consumedMeal.mealtime || guessMealtime());
    setConsumptionTime(consumedMeal.consumptionTime || new Date().toISOString().slice(0, 16));
  }, [consumedMeal]);


  const hasSelection = mealName.trim() !== '' || foodstuffs.length > 0 || mealID.trim() !== ''
  const [showDetails, setShowDetails] = useState<boolean>(false)

  useEffect(() => {
    if (!hasSelection) {
      setShowDetails(false)
    }
  }, [hasSelection])

  useEffect(() => {
    if (mode === 'known' && hasSelection) {
      setShowDetails(isMobile)
    }
  }, [mode, hasSelection, isMobile])

  function guessMealtime(): string {
    const hour = new Date().getHours();
    if (hour < 10) return 'BREAKFAST';
    if (hour < 12) return 'SNACK_AM';
    if (hour < 15) return 'LUNCH';
    if (hour < 18) return 'SNACK_PM';
    return 'DINNER';
  }

  const fetchSavedMeals = async (query: string) => {
    setSavedMealsLoading(true)
    try {
      const normalizedQuery = query.trim() || '%'
      const meals = await MealControllerService.getLikeMeals(normalizedQuery)
      setSavedMeals(meals)
    } catch (error) {
      setSavedMeals([])
      console.error('Failed to fetch saved meals', error)
    } finally {
      setSavedMealsLoading(false)
    }
  }

  const openSavedMeals = () => {
    setStoredMealsOpen(true)
    void fetchSavedMeals(storedMealsQuery.trim())
  }

  const totals = calculateTotals(foodstuffs);

  const handleSave = () => {
    const toSave: ConsumedMeal = {
      ...consumedMeal,
      meal: mealID ? { id: mealID, languageCode: consumedMeal.meal?.languageCode || Meal.languageCode.CS } : undefined,
      name: mealName,
      foodstuffs: buildConsumedMealFoodstuffs(foodstuffs),
      mealtime: mealtime,
      consumptionTime: consumptionTime,
    };
    dispatch(saveConsumedMeal(toSave));
  };

  const resetLocalMealState = () => {
    dispatch(setConsumedMeal({}));
    setMealID('')
    setMealName('')
    setFoodstuffs([])
    setMealtime(guessMealtime())
    setConsumptionTime(new Date().toISOString().slice(0, 16))
  }

  const requestModeSwitch = (nextMode: 'known' | 'adhoc') => {
    if (mode === nextMode) return
    const hasData = hasSelection || foodstuffs.length > 0
    if (hasData) {
      const confirmed = window.confirm(t('record_meal.confirm_switch'))
      if (!confirmed) return
    }
    resetLocalMealState()
    setShowDetails(false)
    setStoredMealsOpen(false)
    setManualFoodstuffOpen(false)
    setMode(nextMode)
  }

  const updateFoodstuffWeight = (index: number, weight: number) => {
    const updated = [...foodstuffs];
    updated[index].weight = weight;
    setFoodstuffs(updated);
  };

  const removeFoodstuff = (index: number) => {
    const updated = [...foodstuffs];
    updated.splice(index, 1)
    setFoodstuffs(updated)
  };

  const nudgeFoodstuffWeight = (index: number, delta: number) => {
    const updated = [...foodstuffs];
    const current = updated[index].weight || 0;
    const next = Math.max(0, current + delta);
    updated[index].weight = next;
    setFoodstuffs(updated);
  };

  function storedMealHandler(meal: Meal) {
    const foodstuffsCopy = meal.foodstuffs?.map((fs) => ({ ...fs })) || [];
    setMealID(meal.id || '')
    setMealName(meal.name || '')
    setFoodstuffs(foodstuffsCopy)
    setMealtime(meal.mealtime || guessMealtime())
    setStoredMealsOpen(false)
  }

  function foodstuffAutocompleteHandler(foodstuff: Foodstuff | null) {
    if (foodstuff) {
      setFoodstuffs([...foodstuffs, foodstuff])
    }
  }

  const filteredSavedMeals = useMemo(() => {
    const query = storedMealsQuery.trim().toLowerCase()
    const result = savedMeals.filter((meal) => (meal.name || '').toLowerCase().includes(query))
    return query ? result : savedMeals
  }, [savedMeals, storedMealsQuery])

  const groupedSavedMeals = useMemo(() => {
    const myMeals: Meal[] = []
    const meals: Meal[] = []

    filteredSavedMeals.forEach((meal) => {
      if (meal.userMealId) {
        myMeals.push(meal)
      } else {
        meals.push(meal)
      }
    })

    return { myMeals, meals }
  }, [filteredSavedMeals])






  const showComposition = mode === 'adhoc' ? foodstuffs.length > 0 : showDetails
  const showSaveBar = hasSelection && (mode === 'known' || foodstuffs.length > 0)

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#2E2A26' }}>
        {t('record_meal.title')}
      </Typography>

      {mode === 'known' && !hasSelection && (
        <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
          <Button
    variant="contained"
            onClick={() => {
              openSavedMeals()
            }}
            sx={{
              backgroundColor: '#7A4A2E',
              '&:hover': { backgroundColor: '#5F3923' },
              fontWeight: 700,
              textTransform: 'none'
            }}
          >
            {t('record_meal.select_saved_meal')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => requestModeSwitch('adhoc')}
            sx={{ color: '#6F675F', borderColor: '#D7CFC6', textTransform: 'none' }}
          >
            {t('record_meal.compose_manual')}
          </Button>
        </Box>
      )}

      {mode === 'known' && hasSelection && (
        <Box sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: '#F7F2EC' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2E2A26' }}>
            {mealName || t('record_meal.unnamed_meal')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6F675F', mt: 0.5 }}>
            {totals.energy.toFixed(0)} kcal | {t('common.nutrient.PROTEIN')}: {totals.protein.toFixed(1)}g | {t('common.nutrient.CARBOHYDRATE')}: {totals.carbs.toFixed(1)}g | {t('common.nutrient.FAT')}: {totals.fat.toFixed(1)}g
          </Typography>
        </Box>
      )}

      {mode === 'adhoc' && (
        <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="overline" sx={{ color: '#6F675F', letterSpacing: 1.5 }}>
              {t('record_meal.mode_compose')}
            </Typography>
            <Button
              variant="text"
              onClick={() => requestModeSwitch('known')}
              sx={{ color: '#6F675F', textTransform: 'none' }}
            >
              {t('record_meal.cancel_manual')}
            </Button>
          </Box>
          <FoodstuffAutocomplete onFoodstuffSelected={(foodstuff) => foodstuffAutocompleteHandler(foodstuff)} />
          <Button
            variant="contained"
            onClick={() => setManualFoodstuffOpen(true)}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#7A4A2E',
              '&:hover': { backgroundColor: '#5F3923' },
              textTransform: 'none'
            }}
          >
            {t('record_meal.add_foodstuff_manual')}
          </Button>
          {foodstuffs.length === 0 && (
            <Typography variant="body2" sx={{ color: '#6F675F' }}>
              {t('record_meal.empty_state')}
            </Typography>
          )}
        </Box>
      )}

      {mode === 'known' && hasSelection && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Button
            variant="text"
            onClick={() => setShowDetails((prev) => !prev)}
            sx={{ color: '#7A4A2E', textTransform: 'none', fontWeight: 600 }}
          >
            {showDetails ? t('record_meal.hide_details') : t('record_meal.show_details')}
          </Button>
        </Box>
      )}

      <Collapse in={showComposition} timeout="auto" unmountOnExit>
        <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
          {t('record_meal.details_heading')}
        </Typography>
        <Box>
          <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
            <FormControl>
              <InputLabel>{t('common.mealtime.label')}</InputLabel>
              <Select
                label={t('common.mealtime.label')}
                value={mealtime}
                onChange={(e) => setMealtime(e.target.value)}
                style={{ width: '150px' }}
              >
                {mealtimeOptions.map((mt) => (
                  <MenuItem key={mt} value={mt}>{t(`common.mealtime.${mt}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('record_meal.consumption_time')}
              type="datetime-local"
              value={consumptionTime}
              onChange={(e) => setConsumptionTime(e.target.value)}
            />
          </Box>
          <Box mb={2} mt={2}>
            <Box mb={2}>
              <TextField
                label={t('record_meal.meal_name')}
                variant="outlined"
                fullWidth
                value={mealName}
                onChange={(e) => setMealName(e.target.value)}
              />
            </Box>
            <Box mt={1} display="flex" flexDirection="column" gap={1.5}>
              {foodstuffs.map((f, i) => {
                const kcal = (f.energyInKcal || 0) * (f.weight || 0)
                return (
                  <Box
                    key={i}
                    sx={{
                      borderRadius: 2,
                      p: 1.75,
                      backgroundColor: '#F7F2EC'
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Typography
                        variant="subtitle1"
                        noWrap
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        {f.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {kcal > 0 ? `${kcal.toFixed(0)} kcal` : ''}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mt={1.5} flexWrap="wrap">
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="large"
                          aria-label={t('record_meal.decrease')}
                          onClick={() => nudgeFoodstuffWeight(i, -10)}
                          sx={{ minWidth: 48, minHeight: 48 }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          label={t('record_meal.weight_grams')}
                          type="number"
                          size="medium"
                          value={f.weight || 0}
                          onChange={(e) => updateFoodstuffWeight(i, parseFloat(e.target.value))}
                          inputProps={{ min: 0, step: 10, inputMode: 'numeric' }}
                          sx={{ width: 140 }}
                        />
                        <IconButton
                          size="large"
                          aria-label={t('record_meal.increase')}
                          onClick={() => nudgeFoodstuffWeight(i, 10)}
                          sx={{ minWidth: 48, minHeight: 48 }}
                        >
                          <AddIcon />
                        </IconButton>
                        <Typography variant="body2">g</Typography>
                      </Box>
                      <IconButton
                        size="large"
                        aria-label={t('record_meal.delete_label')}
                        onClick={() => removeFoodstuff(i)}
                        sx={{ minWidth: 48, minHeight: 48 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )
              })}
            </Box>
            <Box mt={2}>
              <FoodstuffAutocomplete onFoodstuffSelected={(foodstuff) => foodstuffAutocompleteHandler(foodstuff)} />
            </Box>
          </Box>

        </Box>
      </Collapse>

      {showSaveBar && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 3,
            p: 2,
            backgroundColor: '#FBF7F2',
            borderRadius: 2,
            boxShadow: '0 -6px 18px rgba(46, 42, 38, 0.08)'
          }}
        >
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Typography variant="body2" sx={{ color: '#6F675F' }}>
              {totals.energy.toFixed(0)} kcal | {t('common.nutrient.PROTEIN')}: {totals.protein.toFixed(1)}g | {t('common.nutrient.CARBOHYDRATE')}: {totals.carbs.toFixed(1)}g | {t('common.nutrient.FAT')}: {totals.fat.toFixed(1)}g
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {t('record_meal.save_meal')}
            </Button>
          </Box>
        </Box>
      )}

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={storedMealsOpen}
        onClose={() => setStoredMealsOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 420,
            height: isMobile ? '80vh' : '100%'
          }
        }}
      >
        <Box p={2} display="flex" flexDirection="column" gap={1.5}>
          <Typography variant="h6">{t('record_meal.saved_meals_title')}</Typography>
          <TextField
            label={t('record_meal.search_saved_meals')}
            value={storedMealsQuery}
            onChange={(event) => {
              const query = event.target.value
              setStoredMealsQuery(query)
              void fetchSavedMeals(query.trim())
            }}
            fullWidth
          />
        </Box>
        <Divider />
        {savedMealsLoading ? (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              Načítání...
            </Typography>
          </Box>
        ) : (
          (groupedSavedMeals.myMeals.length + groupedSavedMeals.meals.length === 0) ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                {t('record_meal.no_saved_meals')}
              </Typography>
            </Box>
          ) : (
            <List sx={{ px: 1, pb: 2 }}>
              {[
                { label: 'Moje jidla', meals: groupedSavedMeals.myMeals },
                { label: 'Jídla', meals: groupedSavedMeals.meals }
              ].map((group) => {
                if (!group.meals.length) {
                  return null
                }

                return (
                  <Box key={group.label}>
                    <Typography variant="subtitle2" sx={{ px: 1.5, py: 1, color: '#6F675F', fontWeight: 700 }}>
                      {group.label}
                    </Typography>
                    {group.meals.map((meal, index) => {
                      const totals = calculateTotals(meal.foodstuffs || [])
                      const isLast = index === group.meals.length - 1

                      return (
                        <Box key={meal.id || `${meal.name}-${index}`}>
                          <ListItemButton onClick={() => storedMealHandler(meal)}>
                            <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
                              <Typography variant="subtitle1">
                                {meal.name || t('record_meal.unnamed_meal')}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {totals.energy.toFixed(0)} kcal | {t('common.nutrient.PROTEIN')}: {totals.protein.toFixed(1)}g | {t('common.nutrient.CARBOHYDRATE')}: {totals.carbs.toFixed(1)}g | {t('common.nutrient.FAT')}: {totals.fat.toFixed(1)}g
                              </Typography>
                            </Box>
                          </ListItemButton>
                          {!isLast && <Divider />}
                        </Box>
                      )
                    })}
                  </Box>
                )
              })}
            </List>
          )
        )}
      </Drawer>

      <Dialog
        open={manualFoodstuffOpen}
        onClose={() => setManualFoodstuffOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('record_meal.add_foodstuff_manual')}</DialogTitle>
        <DialogContent dividers>
          <AddFoodstuff />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default RecordMeal;

function calculateTotals(foodstuffs: Foodstuff[]) {
  let energy = 0;
  let protein = 0;
  let fat = 0;
  let carbs = 0;

  foodstuffs.forEach((f) => {
    const w = f.weight || 0;
    energy += f.energyInKcal!! * w;
    protein += f.proteinMassFraction!! * w;
    fat += f.fatMassFraction!! * w;
    carbs += f.carbohydrateMassFraction!! * w;
  });

  return { energy, protein, fat, carbs };
}
